'use client';

// @boty/ui - DirectionalIcon component
// Automatically mirrors directional icons in RTL mode

import type { SvgIconProps } from '@mui/material/SvgIcon';
import { useTheme } from '@mui/material/styles';

/**
 * Icons that should mirror horizontally in RTL mode.
 * These are directional icons that indicate forward/backward movement.
 */
const DIRECTIONAL_ICONS = [
  'ArrowBack',
  'ArrowBackIos',
  'ArrowBackIosNew',
  'ArrowForward',
  'ArrowForwardIos',
  'ChevronLeft',
  'ChevronRight',
  'KeyboardArrowLeft',
  'KeyboardArrowRight',
  'NavigateBefore',
  'NavigateNext',
  'Send',
  'Reply',
  'ReplyAll',
  'Redo',
  'Undo',
  'FirstPage',
  'LastPage',
  'ArrowLeft',
  'ArrowRight',
  'TrendingFlat',
  'SubdirectoryArrowLeft',
  'SubdirectoryArrowRight',
] as const;

/**
 * Type for directional icon names
 */
export type DirectionalIconName = (typeof DIRECTIONAL_ICONS)[number];

export interface DirectionalIconProps extends SvgIconProps {
  /**
   * The MUI icon component to render
   */
  Icon: React.ComponentType<SvgIconProps>;
  /**
   * The name of the icon (used to determine if it should mirror)
   */
  iconName: string;
}

/**
 * Check if an icon name is directional and should mirror in RTL
 */
export function isDirectionalIcon(iconName: string): boolean {
  return DIRECTIONAL_ICONS.includes(iconName as DirectionalIconName);
}

/**
 * DirectionalIcon - Renders an icon that automatically mirrors in RTL mode
 *
 * This component should be used for directional icons like arrows and navigation
 * buttons that need to mirror horizontally when the text direction is RTL.
 *
 * Non-directional icons (like checkmarks, close buttons, search icons) should NOT
 * use this component - they should render normally without mirroring.
 *
 * @param Icon - The MUI icon component to render
 * @param iconName - The name of the icon (e.g., 'ArrowForward', 'ChevronLeft')
 * @param sx - Additional styles to apply
 * @param props - Other SvgIconProps passed to the icon
 *
 * @example
 * ```tsx
 * import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
 *
 * function NextButton() {
 *   return (
 *     <DirectionalIcon
 *       Icon={ArrowForwardIcon}
 *       iconName="ArrowForward"
 *       color="primary"
 *     />
 *   );
 * }
 * ```
 */
export function DirectionalIcon({
  Icon,
  iconName,
  sx,
  ...props
}: DirectionalIconProps) {
  const theme = useTheme();
  const shouldMirror =
    theme.direction === 'rtl' && isDirectionalIcon(iconName);

  return (
    <Icon
      {...props}
      sx={{
        ...sx,
        transform: shouldMirror ? 'scaleX(-1)' : undefined,
      }}
    />
  );
}

DirectionalIcon.displayName = 'DirectionalIcon';
